import { createContext, useContext, useState, useMemo, useCallback, ReactNode, useEffect } from 'react';
import realRatingData from '../data/realRatingData.json';
import type { PeriodMode } from '../types/periodMode';
import { getRatedInstitutions, getLatestRating, getRatingHistory, getIndicatorScores } from '../services/institutionService';
import type { RatedInstitution, IndicatorScore, InstitutionRating } from '../types/institution';
import { componentScoresToArray } from '../types/institution';

// Feature flag for dynamic data
const USE_DYNAMIC_DATA = import.meta.env.VITE_DYNAMIC_INSTITUTIONS !== 'false';

// Types
export interface BenchInstitution {
  id: string;
  name: string;
  type?: string;
  segment?: string;
  country?: string;
}

export interface CAMELScore {
  code: string;
  score: number;
}

export interface IndicatorData {
  indicator_id: string;
  indicator_name: string;
  indicator_value: string;
  indicator_score: string;
  indicator_category: string;
}

export interface PeriodData {
  date: string;
  rating: string;
  finalScore: number;
  camel: CAMELScore[];
  sankeyIndicators?: IndicatorData[];
}

export interface InstitutionData {
  institution_name: string;
  rating: string;
  date: string;
  finalScore: number;
  camel: CAMELScore[];
  sankeyIndicators: IndicatorData[];
  history: PeriodData[];
}

export interface RecentReport {
  id: string;
  institutionId: string;
  institutionName: string;
  period: string;
  lastModified: Date;
}

export type NavSection =
  | 'landing'
  | 'company-profile'
  | 'financials'
  | 'key-ratios'
  | 'quantitative-rating'
  | 'qualitative-rating'
  | 'credit-rating'
  | 'reports';

export type RatioCategory = 'C' | 'S' | 'L' | 'EG' | 'R' | 'STI';

// Views that support per-view period selection
export type PeriodView = 'financials' | 'key-ratios' | 'quantitative-rating' | 'credit-rating';

// Map of view to its selected period
export type ViewPeriods = Record<PeriodView, string>;

// Type the imported JSON data
const typedRatingData = realRatingData as Record<string, InstitutionData>;

interface AnalystBenchContextType {
  // Selection state
  selectedInstitution: BenchInstitution | null;
  setSelectedInstitution: (institution: BenchInstitution | null) => void;

  // Per-view period selection (new architecture)
  viewPeriods: ViewPeriods;
  setViewPeriod: (view: PeriodView, period: string) => void;
  getViewPeriod: (view: PeriodView) => string;
  defaultPeriod: string; // Latest available period for selected institution
  availablePeriods: string[]; // All available periods for selected institution

  // Reports view period (set from URL params when navigating from drafts)
  reportsPeriod: string | null;
  setReportsPeriod: (period: string | null) => void;

  // Navigation state
  activeSection: NavSection;
  setActiveSection: (section: NavSection) => void;
  activeRatioCategory: RatioCategory | null;
  setActiveRatioCategory: (category: RatioCategory | null) => void;

  // Recent reports
  recentReports: RecentReport[];
  addRecentReport: (report: RecentReport) => void;

  // Comparison mode
  comparisonInstitutions: BenchInstitution[];
  addComparisonInstitution: (institution: BenchInstitution) => void;
  removeComparisonInstitution: (institutionId: string) => void;
  clearComparison: () => void;

  // Drafting state
  activeDraftSection: string | null;
  setActiveDraftSection: (section: string | null) => void;

  // Period mode for Financials and Key Ratios tables
  financialsPeriodMode: PeriodMode;
  setFinancialsPeriodMode: (mode: PeriodMode) => void;
  keyRatiosPeriodMode: PeriodMode;
  setKeyRatiosPeriodMode: (mode: PeriodMode) => void;
  keyRatiosViewMode: 'cards' | 'table';
  setKeyRatiosViewMode: (mode: 'cards' | 'table') => void;
}

const AnalystBenchContext = createContext<AnalystBenchContextType | undefined>(undefined);

// Available periods (last 13 months)
export function getAvailablePeriods(): string[] {
  const periods: string[] = [];
  const now = new Date();

  for (let i = 0; i < 13; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    periods.push(`${year}-${month}`);
  }

  return periods;
}

// Segment ID to display value mapping
const SEGMENT_MAP: Record<string, string> = {
  'SEG_1': 'S1',
  'SEG_2': 'S2',
  'SEG_3': 'S3',
  'SEG_4': 'S4',
  'SEG_5': 'S5',
  'SEG_MUTUALISTA': 'Mutualista',
  'SEG_APOYO': 'Apoyo',
};

// Build institutions from static JSON data (legacy - for backwards compatibility)
export const BENCH_INSTITUTIONS: BenchInstitution[] = Object.entries(typedRatingData).map(([id, data]) => ({
  id,
  name: data.institution_name.replace('COOPERATIVA DE AHORRO Y CREDITO ', '').replace(' LIMITADA', '').replace(' LTDA', ''),
  type: 'Cooperative',
  segment: 'S1',
  country: 'Ecuador',
}));

// Transform API institution to BenchInstitution format
function transformToBenchInstitution(inst: RatedInstitution): BenchInstitution {
  // Determine institution type from name
  let type = 'Cooperative';
  if (inst.institution_name.includes('MUTUALISTA')) type = 'Mutualista';
  else if (inst.institution_name.includes('CAJA CENTRAL')) type = 'Caja Central';

  // Create short name
  const shortName = inst.institution_name
    .replace(/^COOPERATIVA DE AHORRO Y CREDITO\s*/i, '')
    .replace(/^ASOCIACION MUTUALISTA DE AHORRO Y CREDITO PARA LA VIVIENDA\s*/i, '')
    .replace(/^CAJA CENTRAL\s*/i, '')
    .replace(/\s+LTDA\.?$/i, '')
    .replace(/\s+LIMITADA$/i, '');

  return {
    id: inst.institution_id,
    name: shortName,
    type,
    segment: SEGMENT_MAP[inst.segment_id] || inst.segment_name,
    country: inst.country,
  };
}

// Dynamic institution list (populated from API)
let dynamicInstitutions: BenchInstitution[] = [];
let institutionsFetched = false;

/**
 * Get all institutions (dynamic from API with fallback to static)
 * Use this instead of BENCH_INSTITUTIONS for new code
 */
export function getAllInstitutions(): BenchInstitution[] {
  if (institutionsFetched && dynamicInstitutions.length > 0) {
    return dynamicInstitutions;
  }
  return BENCH_INSTITUTIONS;
}

/**
 * Fetch institutions from API and cache them
 * Call this once at app startup or when needed
 */
export async function fetchInstitutions(): Promise<BenchInstitution[]> {
  try {
    const apiInstitutions = await getRatedInstitutions();
    dynamicInstitutions = apiInstitutions.map(transformToBenchInstitution);
    institutionsFetched = true;
    console.log(`[AnalystBench] Loaded ${dynamicInstitutions.length} institutions from API`);
    return dynamicInstitutions;
  } catch (error) {
    console.warn('[AnalystBench] Failed to fetch institutions from API, using static data:', error);
    return BENCH_INSTITUTIONS;
  }
}

/**
 * Find institution by ID (checks dynamic first, then static)
 */
export function findInstitutionById(institutionId: string): BenchInstitution | undefined {
  // Try dynamic list first
  if (institutionsFetched && dynamicInstitutions.length > 0) {
    const found = dynamicInstitutions.find(i => i.id === institutionId);
    if (found) return found;
  }
  // Fallback to static list
  return BENCH_INSTITUTIONS.find(i => i.id === institutionId);
}

// ============================================================================
// DATA CACHING (for dynamic API data)
// ============================================================================

// Cache for institution data fetched from API
const institutionDataCache = new Map<string, {
  data: InstitutionData;
  timestamp: number;
}>();

// Cache for period data fetched from API
const periodDataCache = new Map<string, {
  data: PeriodData;
  timestamp: number;
}>();

// Cache TTL: 5 minutes (data is already cached on backend for 24 hours)
const CACHE_TTL_MS = 5 * 60 * 1000;

function isCacheValid(timestamp: number): boolean {
  return Date.now() - timestamp < CACHE_TTL_MS;
}

/**
 * Transform API indicator to frontend format
 */
function transformIndicator(indicator: IndicatorScore): IndicatorData {
  return {
    indicator_id: indicator.indicator_id,
    indicator_name: indicator.indicator_name || indicator.indicator_id,
    indicator_value: String(indicator.indicator_value),
    indicator_score: String(indicator.indicator_score),
    indicator_category: indicator.indicator_category || 'Unknown',
  };
}

/**
 * Transform API rating to PeriodData format
 */
function transformRatingToPeriodData(
  rating: InstitutionRating,
  indicators: IndicatorScore[]
): PeriodData {
  return {
    date: rating.period,
    rating: rating.rating_category,
    finalScore: rating.numeric_score,
    camel: componentScoresToArray(rating.component_scores),
    sankeyIndicators: indicators.map(transformIndicator),
  };
}

/**
 * Get data for a specific institution and period (ASYNC - fetches from API)
 * Returns the period-specific data from BigQuery API
 */
export async function getInstitutionPeriodDataAsync(
  institutionId: string,
  period: string
): Promise<PeriodData | null> {
  // If dynamic data is disabled, use static JSON
  if (!USE_DYNAMIC_DATA) {
    return getInstitutionPeriodDataStatic(institutionId, period);
  }

  const cacheKey = `${institutionId}:${period}`;

  // Check cache first
  const cached = periodDataCache.get(cacheKey);
  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data;
  }

  try {
    // Fetch rating and indicators from API
    const [historyResponse, indicatorsResponse] = await Promise.all([
      getRatingHistory(institutionId),
      getIndicatorScores(institutionId, period),
    ]);

    // Find the rating for this period
    const rating = historyResponse.find(r => r.period === period);
    if (!rating) {
      console.warn(`[AnalystBench] No rating found for ${institutionId} period ${period}`);
      // Fallback to static data
      return getInstitutionPeriodDataStatic(institutionId, period);
    }

    const periodData = transformRatingToPeriodData(rating, indicatorsResponse);

    // Cache the result
    periodDataCache.set(cacheKey, {
      data: periodData,
      timestamp: Date.now(),
    });

    return periodData;
  } catch (error) {
    console.error(`[AnalystBench] Error fetching period data for ${institutionId}:`, error);
    // Fallback to static data
    return getInstitutionPeriodDataStatic(institutionId, period);
  }
}

/**
 * Get data for a specific institution and period (SYNC - static JSON fallback)
 */
export function getInstitutionPeriodDataStatic(institutionId: string, period: string): PeriodData | null {
  const institution = typedRatingData[institutionId];
  if (!institution) return null;

  // Check if requesting current period
  if (institution.date === period) {
    return {
      date: institution.date,
      rating: institution.rating,
      finalScore: institution.finalScore,
      camel: institution.camel,
      sankeyIndicators: institution.sankeyIndicators,
    };
  }

  // Look in history
  const historicalData = institution.history?.find(h => h.date === period);
  if (historicalData) {
    return {
      date: historicalData.date,
      rating: historicalData.rating,
      finalScore: historicalData.finalScore,
      camel: historicalData.camel,
      sankeyIndicators: historicalData.sankeyIndicators,
    };
  }

  return null;
}

/**
 * Get data for a specific institution and period
 * DEPRECATED: Use getInstitutionPeriodDataAsync instead for full API support
 * This sync version returns null for institutions not in static JSON
 */
export function getInstitutionPeriodData(institutionId: string, period: string): PeriodData | null {
  return getInstitutionPeriodDataStatic(institutionId, period);
}

/**
 * Get full institution data (ASYNC - fetches from API)
 * Includes all history with rating details
 */
export async function getInstitutionDataAsync(institutionId: string): Promise<InstitutionData | null> {
  // If dynamic data is disabled, use static JSON
  if (!USE_DYNAMIC_DATA) {
    return typedRatingData[institutionId] || null;
  }

  // Check cache first
  const cached = institutionDataCache.get(institutionId);
  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data;
  }

  try {
    // Fetch rating history and latest rating from API
    const [latestRating, ratingHistory] = await Promise.all([
      getLatestRating(institutionId),
      getRatingHistory(institutionId),
    ]);

    if (!latestRating) {
      console.warn(`[AnalystBench] No latest rating found for ${institutionId}`);
      // Fallback to static data
      return typedRatingData[institutionId] || null;
    }

    // Fetch indicators for the latest period
    const indicators = await getIndicatorScores(institutionId, latestRating.period);

    // Find institution name from dynamic list or static
    const benchInstitution = findInstitutionById(institutionId);
    const institutionName = benchInstitution?.name || institutionId;

    // Build history data (without indicators for historical periods to avoid many API calls)
    const historyData: PeriodData[] = ratingHistory
      .filter(r => r.period !== latestRating.period) // Exclude current period
      .map(r => ({
        date: r.period,
        rating: r.rating_category,
        finalScore: r.numeric_score,
        camel: componentScoresToArray(r.component_scores),
        // sankeyIndicators not included for historical periods
      }));

    const institutionData: InstitutionData = {
      institution_name: institutionName,
      rating: latestRating.rating_category,
      date: latestRating.period,
      finalScore: latestRating.numeric_score,
      camel: componentScoresToArray(latestRating.component_scores),
      sankeyIndicators: indicators.map(transformIndicator),
      history: historyData,
    };

    // Cache the result
    institutionDataCache.set(institutionId, {
      data: institutionData,
      timestamp: Date.now(),
    });

    return institutionData;
  } catch (error) {
    console.error(`[AnalystBench] Error fetching institution data for ${institutionId}:`, error);
    // Fallback to static data
    return typedRatingData[institutionId] || null;
  }
}

/**
 * Get full institution data (includes all history)
 * DEPRECATED: Use getInstitutionDataAsync instead for full API support
 * This sync version returns null for institutions not in static JSON
 */
export function getInstitutionData(institutionId: string): InstitutionData | null {
  return typedRatingData[institutionId] || null;
}

/**
 * Get available periods for an institution (ASYNC - fetches from API)
 */
export async function getInstitutionPeriodsAsync(institutionId: string): Promise<string[]> {
  // If dynamic data is disabled, use static JSON
  if (!USE_DYNAMIC_DATA) {
    return getInstitutionPeriodsStatic(institutionId);
  }

  try {
    const history = await getRatingHistory(institutionId);
    const periods = history.map(r => r.period);
    return periods.sort().reverse();
  } catch (error) {
    console.error(`[AnalystBench] Error fetching periods for ${institutionId}:`, error);
    // Fallback to static data
    return getInstitutionPeriodsStatic(institutionId);
  }
}

/**
 * Get available periods for an institution (SYNC - static JSON)
 */
export function getInstitutionPeriodsStatic(institutionId: string): string[] {
  const institution = typedRatingData[institutionId];
  if (!institution) return [];

  const periods = new Set<string>();
  periods.add(institution.date);
  institution.history?.forEach(h => periods.add(h.date));

  return Array.from(periods).sort().reverse();
}

/**
 * Get available periods for an institution
 * DEPRECATED: Use getInstitutionPeriodsAsync for full API support
 */
export function getInstitutionPeriods(institutionId: string): string[] {
  return getInstitutionPeriodsStatic(institutionId);
}

// Default period when no institution selected
const FALLBACK_DEFAULT_PERIOD = '2025-05';

// Create initial view periods with a default
function createInitialViewPeriods(defaultPeriod: string): ViewPeriods {
  return {
    'financials': defaultPeriod,
    'key-ratios': defaultPeriod,
    'quantitative-rating': defaultPeriod,
    'credit-rating': defaultPeriod,
  };
}

export function AnalystBenchProvider({ children }: { children: ReactNode }) {
  // Fetch institutions from API on mount
  useEffect(() => {
    fetchInstitutions().catch(console.error);
  }, []);

  // Selection state - start with null to show home page
  const [selectedInstitution, setSelectedInstitution] = useState<BenchInstitution | null>(null);

  // Per-view period selection (new architecture)
  const [viewPeriods, setViewPeriods] = useState<ViewPeriods>(
    createInitialViewPeriods(FALLBACK_DEFAULT_PERIOD)
  );

  // Reports view period (set from URL params when navigating from drafts)
  const [reportsPeriod, setReportsPeriod] = useState<string | null>(null);

  // Navigation state
  const [activeSection, setActiveSection] = useState<NavSection>('landing');
  const [activeRatioCategory, setActiveRatioCategory] = useState<RatioCategory | null>(null);

  // Recent reports (persisted in localStorage in real app)
  // Seed with sample data for demo purposes using real institution IDs
  const [recentReports, setRecentReports] = useState<RecentReport[]>([
    {
      id: 'COOP_0190155722001-2025-05',
      institutionId: 'COOP_0190155722001',
      institutionName: 'JARDIN AZUAYO',
      period: '2025-05',
      lastModified: new Date(),
    },
    {
      id: 'COOP_1790325083001-2025-05',
      institutionId: 'COOP_1790325083001',
      institutionName: 'ANDALUCIA',
      period: '2025-05',
      lastModified: new Date(Date.now() - 86400000), // 1 day ago
    },
  ]);

  // Comparison mode
  const [comparisonInstitutions, setComparisonInstitutions] = useState<BenchInstitution[]>([]);

  // Drafting state
  const [activeDraftSection, setActiveDraftSection] = useState<string | null>(null);

  // Period mode state for Financials and Key Ratios tables
  const [financialsPeriodMode, setFinancialsPeriodMode] = useState<PeriodMode>('monthly');
  const [keyRatiosPeriodMode, setKeyRatiosPeriodMode] = useState<PeriodMode>('monthly');
  const [keyRatiosViewMode, setKeyRatiosViewMode] = useState<'cards' | 'table'>('cards');

  // State for available periods (fetched asynchronously)
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([FALLBACK_DEFAULT_PERIOD]);

  // Fetch available periods when institution changes
  useEffect(() => {
    if (!selectedInstitution) {
      setAvailablePeriods([FALLBACK_DEFAULT_PERIOD]);
      return;
    }

    let cancelled = false;

    getInstitutionPeriodsAsync(selectedInstitution.id)
      .then(periods => {
        if (!cancelled) {
          setAvailablePeriods(periods.length > 0 ? periods : [FALLBACK_DEFAULT_PERIOD]);
        }
      })
      .catch(error => {
        console.error('[AnalystBench] Error fetching periods:', error);
        if (!cancelled) {
          // Fallback to static data
          setAvailablePeriods(getInstitutionPeriodsStatic(selectedInstitution.id) || [FALLBACK_DEFAULT_PERIOD]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedInstitution]);

  // Default period is the latest available for the institution
  const defaultPeriod = useMemo(() => {
    return availablePeriods[0] || FALLBACK_DEFAULT_PERIOD;
  }, [availablePeriods]);

  // Set period for a specific view
  const setViewPeriod = useCallback((view: PeriodView, period: string) => {
    setViewPeriods(prev => ({
      ...prev,
      [view]: period,
    }));
  }, []);

  // Get period for a specific view
  const getViewPeriod = useCallback((view: PeriodView): string => {
    return viewPeriods[view] || defaultPeriod;
  }, [viewPeriods, defaultPeriod]);

  const addRecentReport = (report: RecentReport) => {
    setRecentReports(prev => {
      // Remove duplicate if exists
      const filtered = prev.filter(r => !(r.institutionId === report.institutionId && r.period === report.period));
      // Add to front, keep max 10
      return [report, ...filtered].slice(0, 10);
    });
  };

  const addComparisonInstitution = (institution: BenchInstitution) => {
    setComparisonInstitutions(prev => {
      if (prev.find(i => i.id === institution.id)) return prev;
      if (prev.length >= 3) return prev; // Max 3 comparisons
      return [...prev, institution];
    });
  };

  const removeComparisonInstitution = (institutionId: string) => {
    setComparisonInstitutions(prev => prev.filter(i => i.id !== institutionId));
  };

  const clearComparison = () => {
    setComparisonInstitutions([]);
  };

  // Handle institution selection - reset all view periods to default
  const handleSetSelectedInstitution = useCallback(async (institution: BenchInstitution | null) => {
    setSelectedInstitution(institution);

    // When institution changes, reset all view periods to the new default
    if (institution) {
      // Use fallback immediately, then update when async periods are fetched
      setViewPeriods(createInitialViewPeriods(FALLBACK_DEFAULT_PERIOD));

      // Fetch periods async and update view periods
      try {
        const periods = await getInstitutionPeriodsAsync(institution.id);
        const newDefault = periods[0] || FALLBACK_DEFAULT_PERIOD;
        setViewPeriods(createInitialViewPeriods(newDefault));

        // Add to recent reports with the actual default period
        addRecentReport({
          id: `${institution.id}-${newDefault}`,
          institutionId: institution.id,
          institutionName: institution.name,
          period: newDefault,
          lastModified: new Date(),
        });
      } catch (error) {
        console.error('[AnalystBench] Error fetching periods on selection:', error);
        // Try static fallback
        const staticPeriods = getInstitutionPeriodsStatic(institution.id);
        const newDefault = staticPeriods[0] || FALLBACK_DEFAULT_PERIOD;
        setViewPeriods(createInitialViewPeriods(newDefault));

        addRecentReport({
          id: `${institution.id}-${newDefault}`,
          institutionId: institution.id,
          institutionName: institution.name,
          period: newDefault,
          lastModified: new Date(),
        });
      }
    } else {
      // Reset to fallback when no institution
      setViewPeriods(createInitialViewPeriods(FALLBACK_DEFAULT_PERIOD));
    }
  }, []);

  return (
    <AnalystBenchContext.Provider
      value={{
        selectedInstitution,
        setSelectedInstitution: handleSetSelectedInstitution,
        viewPeriods,
        setViewPeriod,
        getViewPeriod,
        defaultPeriod,
        availablePeriods,
        reportsPeriod,
        setReportsPeriod,
        activeSection,
        setActiveSection,
        activeRatioCategory,
        setActiveRatioCategory,
        recentReports,
        addRecentReport,
        comparisonInstitutions,
        addComparisonInstitution,
        removeComparisonInstitution,
        clearComparison,
        activeDraftSection,
        setActiveDraftSection,
        financialsPeriodMode,
        setFinancialsPeriodMode,
        keyRatiosPeriodMode,
        setKeyRatiosPeriodMode,
        keyRatiosViewMode,
        setKeyRatiosViewMode,
      }}
    >
      {children}
    </AnalystBenchContext.Provider>
  );
}

export function useAnalystBench() {
  const context = useContext(AnalystBenchContext);
  if (context === undefined) {
    throw new Error('useAnalystBench must be used within an AnalystBenchProvider');
  }
  return context;
}
